import React, { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Card, NotYetImplemented } from '../../components/ui';
import { useAuth } from '../../context/useAuth';
import { useHomeData } from './useHomeData';
import { greeting, roleShort } from './homeData';
import DescribeBox from './DescribeBox';
import StarterList from './StarterList';
import TaskCard from './TaskCard';

const SECTION_LABEL = 'text-[13px] font-bold tracking-wider uppercase text-pencil mb-3';

export default function HomePage() {
  const { search } = useLocation();
  const { user } = useAuth();
  const { loading, profile, starters, tasks, canvases } = useHomeData();
  const [text, setText] = useState('');
  const [deleted, setDeleted] = useState([]);

  if (new URLSearchParams(search).has('workflow')) {
    return <Navigate to={`/runs/open${search}`} replace />;
  }
  const skipped = localStorage.getItem('onboarding_skipped') === '1';
  if (!loading && profile && (!profile.role || !profile.group_function) && !skipped) {
    return <Navigate to="/start" replace />;
  }

  const visible = (tasks || []).filter((t) => !deleted.includes(t.id));
  const short = roleShort(profile && profile.role);

  return (
    <div className="bg-paper text-ink min-h-full">
      <div className="max-w-6xl mx-auto px-6 py-9 flex flex-col gap-9">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">
            {greeting(new Date(), user && user.full_name)}
          </h1>
          <h2 className="font-display text-2xl font-extrabold mt-1">What's next?</h2>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          <Card variant="lifted" className="p-[22px] flex flex-col gap-3">
            <DescribeBox
              value={text}
              onChange={setText}
              label="New task"
              placeholder="Describe what you need done — we'll plan the steps"
            />
            {!loading && <StarterList starters={starters} limit={2} onPick={(s) => setText(s.title)} />}
            <Link to="/start/ways" className="text-sm font-semibold text-ink underline focus-ring">
              {short ? `More ideas for ${short} →` : 'More ideas →'}
            </Link>
          </Card>

          {!loading && (
          <Card variant="lifted" className="p-[22px]">
            <div className={SECTION_LABEL}>Canvas</div>
            <div className="grid grid-cols-2 gap-2.5">
              <Link
                to="/canvas?new=1"
                className="h-24 grid place-items-center text-center font-bold text-sm bg-hl-soft border-1.5 border-dashed border-ink rounded-xl focus-ring"
              >
                + Blank canvas
              </Link>
              {(canvases || []).slice(0, 3).map((c) => (
                <Link
                  key={c.id}
                  to={`/canvas?flow=${encodeURIComponent(c.id)}`}
                  className="h-24 border-1.5 border-rule rounded-xl bg-paper p-2 flex items-end text-[13px] font-semibold focus-ring"
                >
                  {c.title}
                </Link>
              ))}
            </div>
          </Card>
          )}
        </div>

        {!loading && (
        <section>
          <div className="flex items-baseline justify-between mb-3.5 gap-3 flex-wrap">
            <h2 className="font-display text-2xl font-extrabold">Your tasks</h2>
            <div className="flex items-center gap-3 text-sm text-pencil flex-wrap">
              <span>{visible.length} saved · newest first</span>
              <NotYetImplemented feature="taskLastRun" className="flex items-center gap-2 [&>div]:mb-0">
                <span className="text-pencil">Last run</span>
              </NotYetImplemented>
              <Link to="/tasks" className="text-ink underline focus-ring">See all</Link>
            </div>
          </div>
          {visible.length === 0 ? (
            <p className="text-pencil">No saved tasks yet.</p>
          ) : (
            <div className="grid gap-[18px] sm:grid-cols-2 lg:grid-cols-3">
              {visible.slice(0, 6).map((t) => (
                <TaskCard key={t.id} task={t} onDeleted={(id) => setDeleted((d) => [...d, id])} />
              ))}
            </div>
          )}
        </section>
        )}
      </div>
    </div>
  );
}
